import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useParams } from 'react-router';
import { ArrowLeftIcon } from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  INTERVIEW_PANEL_WIDTH_CLASS_NAME,
  SIDE_PANEL_FULLSCREEN_WIDTH_CLASS_NAME,
  SIDE_PANEL_SPLIT_WIDTH_CLASS_NAME,
} from '@/shared/ui/layout/layout.constants';
import { SidePanelContainer } from '@/shared/ui/layout/SidePanel';
import { SessionContentLayout } from './SessionContentLayout';
import { InterviewChatHeader } from './InterviewChatHeader';
import { RedesignedChatHeader } from './RedesignedChatHeader';
import { useChatTitle } from './hooks/useChatTitle';
import type {
  InterviewPersonaLike,
  PersonaDetailPersonaLike,
  SessionsAppDeps,
  SessionsAppShellDeps,
  SessionsChatsDeps,
  SessionsInterviewDeps,
  SessionsLayoutMode,
  SessionsOutletContext,
} from './SessionsLayout.types';
import { useTranslation } from 'react-i18next';

/**
 * The widget's width for a layout mode. A hidden widget is sized by the mode it comes back
 * in, so it slides out at the size it had.
 */
function widgetWidthClassName(layoutMode: SessionsLayoutMode) {
  return layoutMode === 'fullscreen'
    ? SIDE_PANEL_FULLSCREEN_WIDTH_CLASS_NAME
    : SIDE_PANEL_SPLIT_WIDTH_CLASS_NAME;
}

/**
 * The bits of session shell (header, panels, width classes) that differ
 * between the interview and redesigned modes. Both `buildInterviewShell` /
 * `buildRedesignedShell` produce one of these so the JSX below stays a single,
 * stable `SessionContentLayout` render.
 */
type SessionShell = {
  header: ReactNode;
  leftPanel: ReactNode;
  leftPanelTestId: string;
  leftPanelContentClassName: string;
  isLeftPanelOpen: boolean;
  isLeftPanelFullscreen: boolean;
  renderLeftPanel: boolean;
  chatAreaClassName?: string;
};

/**
 * `SessionContent`: single session shell for BOTH the interview
 * (audience) shell and the redesigned shell.
 *
 * Rendering ONE stable component type (instead of swapping between two
 * distinct components) is deliberate: the chat subtree lives in the
 * `children` slot of `SessionContentLayout`, so as long as this component
 * and that layout keep the same identity across an in-session agent switch,
 * React preserves the `<Outlet>` subtree (Root / StreamChatSession) instead
 * of remounting it. A remount here used to reset the "run once" refs in the
 * chat pages and revert the user's agent switch.
 *
 * Only the SHELL (header, left panel, width classes) varies by
 * `isInterviewSession`. All hooks are called unconditionally to keep hook
 * order stable; the persona queries are gated by `isInterviewSession` so the
 * redesigned mode does not fetch personas it never shows.
 */
export function SessionContent({
  isInterviewSession,
  app,
  chats,
  interview,
  selectedWorkspaceId,
  appShell,
  outletContext,
  children,
}: {
  isInterviewSession: boolean;
  app: SessionsAppDeps;
  chats: SessionsChatsDeps;
  interview: SessionsInterviewDeps;
  selectedWorkspaceId: string | null | undefined;
  appShell: SessionsAppShellDeps;
  outletContext: SessionsOutletContext;
  children?: ReactNode;
}) {
  const { t } = useTranslation('chats');

  const isSidePanelOpen = app.useIsSidePanelOpenHook();
  const sidePanelType = app.useSidePanelTypeHook();
  const { sidePanelLayoutMode, setSidePanelLayoutMode } = app.useSidePanelLayoutModeHook();
  const closeSidePanel = app.useCloseSidePanelHook();
  const lastLayoutMode = app.useLastNonNullLayoutModeHook();

  const { chatTitle, commitTitle } = useChatTitle(chats.getChatInfo);

  // Interview shell state — always declared to keep hook order stable. The
  // persona queries are gated by `isInterviewSession` so redesigned mode does
  // not fetch (an empty `personasQuery.data` just resolves the persona to null).
  // Only for the personas panel. A widget's hidden state lives in the UI store, so any
  // open, close or clear of the widget elsewhere supersedes it.
  const [isPersonasHidden, setIsPersonasHidden] = useState(false);
  const [explicitPick, setExplicitPick] = useState<{
    persona: InterviewPersonaLike;
    chatPersonaIdAtPick: string | null;
  } | null>(null);
  const [isBrowsingPersonas, setIsBrowsingPersonas] = useState(false);

  // A pick, or browsing the list, belongs to the chat it happened in. The layout stays
  // mounted across a chat switch, so the next chat would otherwise show the previous pick.
  const { chatId } = useParams<{ chatId: string }>();
  // Moving from one chat to another, the store keeps the previous chat's agent and panel
  // until the new one's history loads, so the panel stays closed rather than show them.
  // The new-session screen resets the store (null), so a chat it starts shows the widget it
  // was opened with at once; a chat started from any other page waits for its history too.
  const loadedChatId = chats.useChatIdHook();
  const isPreviousChatShowing = Boolean(chatId) && loadedChatId !== null && loadedChatId !== chatId;
  const [pickChatId, setPickChatId] = useState(chatId);
  if (chatId !== pickChatId) {
    setPickChatId(chatId);
    setExplicitPick(null);
    setIsBrowsingPersonas(false);
  }

  const selectedPersonaIdFromChat = interview.useSelectedPersonaIdFromChat();

  // A panel pick only bridges the gap until the chat hands off to it. Once the chat names
  // a different persona than at pick time, the chat wins. The pick is cleared rather than
  // just ignored, so a later switch back to the old persona cannot revive it.
  const chatMovedSincePick =
    explicitPick !== null &&
    selectedPersonaIdFromChat !== null &&
    selectedPersonaIdFromChat !== explicitPick.chatPersonaIdAtPick;
  if (chatMovedSincePick) setExplicitPick(null);
  const explicitPersona = chatMovedSincePick ? null : (explicitPick?.persona ?? null);

  const personasQuery = interview.usePersonas(selectedWorkspaceId, isInterviewSession);
  const fullPersonasQuery = interview.useFullPersonas(selectedWorkspaceId, isInterviewSession);

  // Derived, not stored
  const selectedPersona = useMemo<InterviewPersonaLike | null>(() => {
    if (isBrowsingPersonas) return null;
    if (explicitPersona) return explicitPersona;
    if (!selectedPersonaIdFromChat || !personasQuery.data) return null;
    return (
      personasQuery.data.find((candidate) => candidate.id === selectedPersonaIdFromChat) ?? null
    );
  }, [isBrowsingPersonas, explicitPersona, selectedPersonaIdFromChat, personasQuery.data]);

  const selectedFullPersona = useMemo<PersonaDetailPersonaLike | null>(() => {
    if (!selectedPersona || !fullPersonasQuery.data) return null;
    return (
      fullPersonasQuery.data.find(
        (persona: PersonaDetailPersonaLike) => persona.id === selectedPersona.id,
      ) ?? null
    );
  }, [selectedPersona, fullPersonasQuery.data]);

  const handlePersonaSelect = useCallback(
    (persona: InterviewPersonaLike) => {
      setIsBrowsingPersonas(false);
      setExplicitPick({ persona, chatPersonaIdAtPick: selectedPersonaIdFromChat });
      interview.onPersonaSelect(persona);
    },
    [interview, selectedPersonaIdFromChat],
  );

  // A widget with no layout mode is hidden, the same reading as the redesigned shell and
  // the agent's UI context, which then leaves the widget out.
  const hasWidget = isSidePanelOpen && sidePanelType === 'widget';
  const isWidgetVisible = hasWidget && sidePanelLayoutMode !== null;
  const isWidgetHidden = hasWidget && sidePanelLayoutMode === null;
  const isPersonasPanelVisible = !hasWidget && !isPersonasHidden;

  const handleToggleSidePanel = useCallback(() => {
    // The toggle reads closed while the previous chat is masked, and the state behind the mask
    // is that chat's: acting on it would hide the next chat's panel before it shows.
    if (isPreviousChatShowing) return;
    if (isWidgetVisible) {
      setSidePanelLayoutMode(null);
      return;
    }
    if (isWidgetHidden) {
      setSidePanelLayoutMode(lastLayoutMode);
      return;
    }
    setIsPersonasHidden((hidden) => !hidden);
  }, [
    isPreviousChatShowing,
    isWidgetVisible,
    isWidgetHidden,
    setSidePanelLayoutMode,
    lastLayoutMode,
  ]);

  const handleBackToPersonas = useCallback(() => {
    setIsBrowsingPersonas(true);
    setExplicitPick(null);
  }, []);

  const AppSidebar = appShell.AppSidebar;
  // One element across renders, so toggling a panel does not re-render the app sidebar.
  const appSidebar = useMemo(() => <AppSidebar />, [AppSidebar]);
  const { InterviewPanel, PersonaDetail } = interview;

  // Stable chat content element — the same node in the same slot regardless of
  // which shell is active, so agent switches never remount it.
  const chatContent = children ?? <Outlet context={outletContext} />;

  function buildInterviewShell(): SessionShell {
    const backToPersonasLabel = t('sessions.sessionContent.backToPersonas');
    const isPanelShown = !isPreviousChatShowing && (isWidgetVisible || isPersonasPanelVisible);
    const isWidgetFullscreen =
      !isPreviousChatShowing && isWidgetVisible && sidePanelLayoutMode === 'fullscreen';

    return {
      leftPanelTestId: 'interview-personas-panel',
      isLeftPanelOpen: isPanelShown,
      isLeftPanelFullscreen: isWidgetFullscreen,
      // Closed, the panel stays mounted (inert), so it slides out rather than vanishing and
      // the personas panel keeps where the user was.
      renderLeftPanel: true,
      leftPanelContentClassName: hasWidget
        ? widgetWidthClassName(sidePanelLayoutMode ?? lastLayoutMode)
        : INTERVIEW_PANEL_WIDTH_CLASS_NAME,
      leftPanel:
        // A hidden widget stays mounted so Show returns to the inner view the user left (its
        // navigation is component state).
        hasWidget ? (
          <div className="h-full">
            <SidePanelContainer closeSidePanel={closeSidePanel} type="widget" />
          </div>
        ) : selectedFullPersona ? (
          <div className="flex h-full flex-col overflow-hidden rounded-lg bg-background">
            <div className="flex items-center gap-2 border-b border-border px-3 py-3">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-lg"
                      aria-label={backToPersonasLabel}
                      onClick={handleBackToPersonas}
                    >
                      <ArrowLeftIcon className="size-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{backToPersonasLabel}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-base font-medium leading-6 text-foreground">
                {t('sessions.sessionContent.personasOverview')}
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto px-6 py-6">
              <div className="w-full [&>div]:max-w-full [&>div]:justify-self-start">
                <PersonaDetail persona={selectedFullPersona} compact />
              </div>
            </div>
          </div>
        ) : (
          <InterviewPanel
            personas={personasQuery.data ?? []}
            onPersonaSelect={handlePersonaSelect}
            onBack={interview.onNavigateBack}
            isLoading={personasQuery.isLoading}
          />
        ),
      header: (
        <InterviewChatHeader
          onToggleSidePanel={handleToggleSidePanel}
          isSidePanelVisible={isPanelShown}
          interview={interview}
          persona={selectedPersona}
          chatTitle={chatTitle}
          onRename={commitTitle}
        />
      ),
      chatAreaClassName: isWidgetFullscreen ? 'hidden' : undefined,
    };
  }

  function buildRedesignedShell(): SessionShell {
    const isPanelVisible =
      !isPreviousChatShowing &&
      isSidePanelOpen &&
      sidePanelType != null &&
      sidePanelLayoutMode !== null;
    const isWidgetFullscreen = isPanelVisible && sidePanelLayoutMode === 'fullscreen';

    return {
      leftPanelTestId: 'sessions-side-panel',
      isLeftPanelOpen: isPanelVisible,
      isLeftPanelFullscreen: isWidgetFullscreen,
      renderLeftPanel: isSidePanelOpen && sidePanelType != null,
      leftPanelContentClassName: widgetWidthClassName(sidePanelLayoutMode ?? lastLayoutMode),
      leftPanel:
        sidePanelType != null ? (
          <SidePanelContainer closeSidePanel={closeSidePanel} type={sidePanelType} />
        ) : null,
      header: <RedesignedChatHeader chatTitle={chatTitle} onRename={commitTitle} />,
      chatAreaClassName: isWidgetFullscreen ? 'hidden' : undefined,
    };
  }

  const shell = isInterviewSession ? buildInterviewShell() : buildRedesignedShell();

  return (
    <SessionContentLayout
      appSidebar={appSidebar}
      leftPanel={shell.leftPanel}
      leftPanelTestId={shell.leftPanelTestId}
      leftPanelContentClassName={shell.leftPanelContentClassName}
      isLeftPanelOpen={shell.isLeftPanelOpen}
      isLeftPanelFullscreen={shell.isLeftPanelFullscreen}
      renderLeftPanel={shell.renderLeftPanel}
      header={shell.header}
      chatAreaClassName={shell.chatAreaClassName}
    >
      {chatContent}
    </SessionContentLayout>
  );
}
